import { createContext } from 'react';
import { observable, action, computed } from 'mobx';
import { ExamReadinessInfo, ExamStatus, ExamCollateral } from 'types/exam';
import { ExamType, CatalogEntry } from 'types';
import { EXAM_FAQ_PLAYLIST_SLUG } from 'config/constants';

import { updateVideoProgress } from 'services/ProgressService';
import {
  cancelExam,
  getExamCollateral,
  getExamStatus,
  enrollIntoExam,
  getExamFaqPlaylist,
  getExamChecklists,
} from '../services/ExamService';
import CatalogStore from './Catalog';

class ExamStore {
  catalogStore: CatalogStore;

  constructor(catalogStore) {
    this.catalogStore = catalogStore;
  }

  @observable canShowSuccessAlert = false;

  @observable code = '';

  @observable collateral: ExamCollateral = {};

  @observable disableExamOperations = false;

  @observable enrollmentLoading = false;

  @observable isLoadingCollateral = false;

  @observable interval = 10000;

  @observable IESURL = '';

  @observable loading = false;

  @observable userExam: any = {};

  @observable isUserExamLoaded = false;

  @observable version = null;

  @observable isFaqLoaded = false;

  @observable faqPlaylist: any = {};

  @observable isChecklistLoaded = false;

  @observable checklistVersions = [];

  @observable objectivesChecklist: ExamReadinessInfo['checklist'] = {};

  @observable completionChecklist: ExamReadinessInfo['checklist_complete'] = [];

  @observable readinessTraining: ExamReadinessInfo['readiness_training'] = [];

  intervalId: NodeJS.Timeout | null = null;

  @computed get offering(): CatalogEntry {
    // when null, loading will show
    if (!this.code) {
      return null;
    }

    const entry =
      this.catalogStore.uniqueCatalogEntries[this.code] ||
      this.catalogStore.allUniqueCatalogEntries[this.code];

    if (entry) {
      return entry;
    }

    // when undefined, empty state will show
    return undefined;
  }

  @computed get exam(): any {
    return this.userExam;
  }

  @computed get status() {
    return this.exam?.status || ExamStatus.Unenrolled;
  }

  @computed get statusUpperCase() {
    return this.status.toUpperCase();
  }

  @computed get isPreliminary() {
    const examReference = this.offering?.sku || `${this.code.toUpperCase()}`;
    return examReference.toUpperCase().substring(0, 2) === 'PE';
  }

  @computed get isExperience(): boolean {
    return this.collateral?.exam_type === ExamType.Experience;
  }

  @action getUserExam = async () => {
    if (!this.offering?.sku) {
      return;
    }

    try {
      const data = await getExamStatus(this.offering.sku, this.isExperience);
      this.userExam = data.user_exam;
      this.isUserExamLoaded = true;
    } catch (error) {
      console.error(error);
    }
  };

  @action getExamCollateral = async (code, language) => {
    try {
      this.isLoadingCollateral = true;
      let collateral = await getExamCollateral(code, language);

      if (!collateral) {
        collateral = await getExamCollateral(code);
      }

      this.isLoadingCollateral = false;
      this.collateral = collateral;

      return collateral;
    } catch (error) {
      this.isLoadingCollateral = false;
      throw Error(error);
    }
  };

  @action getExamFaqPlaylist = async () => {
    try {
      const playlistInfo = await getExamFaqPlaylist();

      if (playlistInfo?.kaltura_playlists?.length > 0) {
        this.faqPlaylist = playlistInfo;

        this.isFaqLoaded = true;
      } else {
        this.isFaqLoaded = false;
      }
    } catch (error) {
      this.isFaqLoaded = false;
    }
  };

  @action updateFaqPlaylistProgress = async (player) => {
    if (this.isFaqLoaded && player) {
      const slug = EXAM_FAQ_PLAYLIST_SLUG;

      const entryId = this.faqPlaylist?.kaltura_playlists?.[0]?.id;

      if (entryId) {
        const progress = {
          [entryId]: 1,
        };
        const currentVideo = player.sources.id;
        const timestamp = player.normalizedCurrentTime;

        await updateVideoProgress(slug, progress, currentVideo, timestamp);

        this.getExamFaqPlaylist();
      }
    }
  };

  @action getExamChecklists = async () => {
    try {
      const checklistInfo = await getExamChecklists(this.code);

      if (checklistInfo.checklist) {
        this.objectivesChecklist = checklistInfo.checklist;

        // sort for versions by epoch
        const sortedVersions = Object.entries(checklistInfo.checklist)
          .sort((a, b) => {
            if (a[1].length === 0 || b[1].length === 0) {
              // version doesn't have checklist
              return -1; // leave in the same order
            }

            // selecting 1st checklist since each version
            // can have multiple based on language
            // using 0 as default since sorting in ascending
            const epochA = a[1][0].epoch_number ?? 0;
            const epochB = b[1][0].epoch_number ?? 0;

            return epochB - epochA;
          })
          .map((e) => e[0]);

        this.checklistVersions = sortedVersions;
      }

      if (checklistInfo.checklist_complete) {
        this.completionChecklist = checklistInfo.checklist_complete;
      }

      if (checklistInfo.readiness_training) {
        this.readinessTraining = checklistInfo.readiness_training;
      }

      this.isChecklistLoaded = true;
    } catch (error) {
      this.isChecklistLoaded = false;
    }
  };

  @action enroll = async (sku: string) => {
    this.enrollmentLoading = true;
    const url = this.IESURL;
    try {
      await enrollIntoExam(sku, this.isExperience);
    } catch (err) {
      throw err;
    } finally {
      this.enrollmentLoading = false;
    }
    return url;
  };

  @action cancelExam = async (slug: string) => {
    this.enrollmentLoading = true;
    try {
      await cancelExam(slug);
    } catch (err) {
      throw err;
    } finally {
      this.enrollmentLoading = false;
    }
  };

  startPolling() {
    this.intervalId = setInterval(this.getUserExam.bind(this), this.interval);
  }

  stopPolling() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
}

export const ExamStoreContext = createContext(null);
export default ExamStore;
